import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Wallet, Send, QrCode, ArrowUpRight, ArrowDownLeft, Copy, Check,
  Crown, LogOut, User, Bell, RefreshCw, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const WalletPage = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({ to_wallet_id: '', amount: '', description: '' });
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/wallets`, { headers, withCredentials: true }),
        axios.get(`${API}/wallets/transactions`, { headers, withCredentials: true })
      ]);
      
      setWallets(walletsRes.data);
      setTransactions(transactionsRes.data);
      if (walletsRes.data.length > 0) {
        setSelectedWallet(walletsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/wallets/transfer`, {
        to_wallet_id: transferData.to_wallet_id,
        amount: parseFloat(transferData.amount),
        description: transferData.description
      }, { headers, withCredentials: true });
      
      toast.success('Transfer completed successfully!');
      setTransferOpen(false);
      setTransferData({ to_wallet_id: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };

  const generateQR = async (walletId) => {
    try {
      const response = await axios.get(`${API}/wallets/qr/${walletId}`, { headers, withCredentials: true });
      setQrCode(response.data.qr_code);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const copyWalletId = () => {
    if (selectedWallet) {
      navigator.clipboard.writeText(selectedWallet.wallet_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Wallet ID copied!');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="wallet-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-lg font-semibold text-primary hidden sm:block">Royal CBDC</span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/citizen">
                <Button variant="ghost">Dashboard</Button>
              </Link>
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-normal text-primary">
              CBDC Wallet
            </h1>
            <p className="text-muted-foreground mt-1">Manage your digital currency</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="mt-4 md:mt-0 gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Wallet Overview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Main Wallet Card */}
            {selectedWallet && (
              <Card className="sovereign-card bg-primary text-primary-foreground">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-8">
                    <div>
                      <p className="text-primary-foreground/70 text-sm uppercase tracking-wider mb-1">Available Balance</p>
                      <p className="font-mono text-4xl md:text-5xl font-semibold" data-testid="wallet-balance">
                        {selectedWallet.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        <span className="text-xl ml-2">CBDC</span>
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                      <Wallet className="h-7 w-7" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                      <DialogTrigger asChild>
                        <Button variant="secondary" className="gap-2" data-testid="send-btn">
                          <Send className="h-4 w-4" /> Send
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Send CBDC</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleTransfer} className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label>Recipient Wallet ID</Label>
                            <Input
                              placeholder="Enter wallet ID"
                              value={transferData.to_wallet_id}
                              onChange={(e) => setTransferData({ ...transferData, to_wallet_id: e.target.value })}
                              required
                              data-testid="transfer-wallet-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Amount (CBDC)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={transferData.amount}
                              onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                              required
                              data-testid="transfer-amount-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Description (Optional)</Label>
                            <Input
                              placeholder="Payment for..."
                              value={transferData.description}
                              onChange={(e) => setTransferData({ ...transferData, description: e.target.value })}
                              data-testid="transfer-description-input"
                            />
                          </div>
                          <Button type="submit" className="w-full" data-testid="transfer-submit-btn">
                            Send Payment
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="secondary" className="gap-2" onClick={() => generateQR(selectedWallet.wallet_id)} data-testid="receive-btn">
                          <QrCode className="h-4 w-4" /> Receive
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Receive Payment</DialogTitle>
                        </DialogHeader>
                        <div className="text-center py-4">
                          {qrCode ? (
                            <img src={qrCode} alt="Payment QR Code" className="mx-auto w-48 h-48" />
                          ) : (
                            <div className="w-48 h-48 bg-muted animate-pulse mx-auto rounded-lg"></div>
                          )}
                          <p className="mt-4 text-sm text-muted-foreground">Scan to send payment</p>
                          <p className="font-mono text-xs mt-2 bg-muted p-2 rounded break-all">
                            {selectedWallet.wallet_id}
                          </p>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Transaction History */}
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg font-semibold">Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No transactions yet</p>
                    <p className="text-sm mt-1">Make your first payment to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((txn, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        data-testid={`transaction-${idx}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            txn.from_wallet_id === selectedWallet?.wallet_id 
                              ? 'bg-red-500/10' 
                              : 'bg-green-500/10'
                          }`}>
                            {txn.from_wallet_id === selectedWallet?.wallet_id 
                              ? <ArrowUpRight className="h-6 w-6 text-red-500" />
                              : <ArrowDownLeft className="h-6 w-6 text-green-500" />
                            }
                          </div>
                          <div>
                            <p className="font-medium">
                              {txn.from_wallet_id === selectedWallet?.wallet_id ? 'Sent' : 'Received'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {txn.description || 'CBDC Transfer'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 font-mono">
                              {txn.transaction_id}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono text-lg font-semibold ${
                            txn.from_wallet_id === selectedWallet?.wallet_id ? 'text-red-500' : 'text-green-500'
                          }`}>
                            {txn.from_wallet_id === selectedWallet?.wallet_id ? '-' : '+'}{txn.amount.toFixed(2)} CBDC
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(txn.created_at).toLocaleDateString()} {new Date(txn.created_at).toLocaleTimeString()}
                          </p>
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium uppercase ${
                            txn.status === 'completed' ? 'status-active' : 'status-pending'
                          }`}>
                            {txn.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Wallet Details */}
            {selectedWallet && (
              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg font-semibold">Wallet Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Wallet ID</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-muted p-2 rounded truncate">
                        {selectedWallet.wallet_id}
                      </code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={copyWalletId}
                        data-testid="copy-wallet-id"
                      >
                        {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Type</span>
                    <span className="font-medium capitalize">{selectedWallet.wallet_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Currency</span>
                    <span className="font-medium">{selectedWallet.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium status-active">
                      Active
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Created</span>
                    <span className="text-sm">
                      {new Date(selectedWallet.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Wallets */}
            {wallets.length > 1 && (
              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg font-semibold">Your Wallets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {wallets.map((wallet, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedWallet(wallet)}
                        className={`w-full p-3 rounded-lg text-left transition-colors ${
                          selectedWallet?.wallet_id === wallet.wallet_id
                            ? 'bg-primary/10 border border-primary/30'
                            : 'bg-muted/30 hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium capitalize">{wallet.wallet_type}</p>
                            <p className="text-xs text-muted-foreground">{wallet.currency}</p>
                          </div>
                          <p className="font-mono font-semibold">
                            {wallet.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg font-semibold">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/bonds" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Plus className="h-4 w-4" /> Buy Bonds
                  </Button>
                </Link>
                <Link to="/public" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Wallet className="h-4 w-4" /> View Rates
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WalletPage;
